import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faGear, faFileImport, faPrint } from "@fortawesome/free-solid-svg-icons";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import { Option } from "components/OptionsCard/typings";
import useDocumentEditor from "hooks/useDocumentEditor";
import radixService from "api/radixService";
import { ItemKind } from "dto/IKindItems";
import { IDispatchListItem } from "dto/Mailroom/IDispatchChart";
import {
  ERadixInternalStatus,
  ERadixListView,
  ERadixProgram,
  IRadixDocumentsActionResponse,
  IRadixListBatch,
  IRadixListDocument,
} from "dto/Radix/IRadix";
import { ENotificationType, notification } from "utils/notification";
import { radixErrorText } from "./radixUtils";

import ChangeDeliveryKindModal from "modules/biuroPodawczeModule/ChangeDeliveryKindModal";
import ParcelPreviewModal from "modules/biuroPodawczeModule/ParcelPreviewModal";

import ImportRadixModal from "./ImportRadixModal";
import RadixPrintModal from "./RadixPrintModal";
import RadixRowMenu from "./RadixRowMenu";
import RadixSettingsModal from "./RadixSettingsModal";
import RadixSourceListModal from "./RadixSourceListModal";
import styles from "./radix.module.scss";

interface Props {
  view: ERadixListView;
  /** Program listy (Decyzje/Upomnienia/Zawiadomienia); null dla Do wysłania / Wysłane. */
  program: ERadixProgram | null;
  searchText: string;
}

const PROGRAM_LABEL: Record<ERadixProgram, string> = {
  [ERadixProgram.WIP]: "WIP",
  [ERadixProgram.PoGrun]: "PoGrun",
  [ERadixProgram.GOK]: "GOK",
};

const IMPORT_LABEL: Record<ERadixProgram, string> = {
  [ERadixProgram.WIP]: "Importuj upomnienia",
  [ERadixProgram.PoGrun]: "Importuj decyzje",
  [ERadixProgram.GOK]: "Importuj zawiadomienia",
};

const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString("pl-PL") : "—");

// Lista paczek → dokumentów (Magistrat: drzewo paczka → [rodzaj przesyłki →] dokumenty).
// W widokach programów paczka to jedna lista pism; w „Do wysłania" / „Wysłane" pisma są
// pogrupowane rodzajem przesyłki (magistracki ParcelInfo „Brak rodzaju przesyłki (n)").
const RadixList = ({ view, program, searchText }: Props) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, refetch } = radixService.useGetList(view, searchText);
  const { data: settingsData } = radixService.useGetSettings();
  const settings = settingsData?.data;

  const { mutate: setReady } = radixService.useSetReadyToDispatch();
  const { mutate: returnToProject } = radixService.useReturnToProject();
  const { mutate: markAsSent } = radixService.useMarkAsSent();
  const { mutate: returnToSend } = radixService.useReturnToSend();
  const { mutate: remove } = radixService.useRemove();
  const { mutate: generateForAll } = radixService.useGenerateForAllReceivers();
  const { openEditor } = useDocumentEditor();

  const batches: IRadixListBatch[] = useMemo(() => data?.data?.Batches ?? [], [data]);
  const isTeamView = data?.data?.IsTeamView ?? false;

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [showImport, setShowImport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const [preview, setPreview] = useState<{ parcelId: string; title?: string | null } | null>(null);
  const [printImport, setPrintImport] = useState<{ id: string; name?: string | null } | null>(null);
  const [changeKind, setChangeKind] = useState<IDispatchListItem[] | null>(null);

  useEffect(() => setSelected(new Set()), [view, searchText]);

  const isProgramView = program !== null;
  const isToSend = view === ERadixListView.ToSend;
  const isSent = view === ERadixListView.Sent;

  const allDocs = useMemo(() => batches.flatMap((b) => b.Documents), [batches]);
  const selectedDocs = allDocs.filter((d) => selected.has(d.Id));

  const invalidate = () => {
    queryClient.invalidateQueries(["radix.list"]);
    queryClient.invalidateQueries(["outgoing.categories"]);
    queryClient.invalidateQueries(["outgoing.dispatchList"]);
    setSelected(new Set());
  };

  const report = (title: string, r: IRadixDocumentsActionResponse) => {
    notification({
      type: r.Affected > 0 ? ENotificationType.SUCCESS : ENotificationType.INFO,
      title,
      text: `Zmieniono: ${r.Affected}${r.Skipped > 0 ? `. ${r.Message ?? `Pominięto: ${r.Skipped}`}` : ""}`,
    });
    invalidate();
  };

  const run = (
    mutate: typeof setReady,
    request: { ImportId?: string | null; DocumentIds?: string[] | null },
    title: string,
    confirm?: string,
  ) => {
    if (confirm && !window.confirm(confirm)) return;
    mutate(request, {
      onSuccess: (res) => report(title, res.data),
      onError: (e) =>
        notification({ type: ENotificationType.ERROR, title: "Błąd", text: radixErrorText(e, "Operacja nie powiodła się.") }),
    });
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleCollapsed = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openInEditor = async (doc: IRadixListDocument) => {
    if (!doc.EditorDocumentId) return;
    await openEditor({ Id: doc.EditorDocumentId, Kind: ItemKind.EditorDocument });
  };

  const doGenerateForAll = (doc: IRadixListDocument) => {
    if (!window.confirm("Czy chcesz wygenerować decyzje dla wszystkich współwłaścicieli?")) return;
    generateForAll(
      { DocumentId: doc.Id },
      {
        onSuccess: (res) => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Kopie decyzji",
            text: res.data.Created > 0 ? `Utworzono kopie: ${res.data.Created}.` : "Wszyscy współwłaściciele mają już swoje pisma.",
          });
          invalidate();
        },
        onError: (e) =>
          notification({ type: ENotificationType.ERROR, title: "Wystąpił błąd podczas generowania decyzji", text: radixErrorText(e, "") }),
      },
    );
  };

  /** Element listy biura podawczego zbudowany z dokumentu RADIX — do modala zmiany sposobu nadania. */
  const toDispatchItem = (d: IRadixListDocument): IDispatchListItem => ({
    Id: d.ParcelId!,
    ParcelNumber: d.ParcelNumber ?? null,
    Title: d.Title ?? null,
    ReceiverName: d.ReceiverName ?? null,
    ReceiverAddress: d.ReceiverAddress ?? null,
    DeliveryKindId: d.DeliveryKindId ?? null,
    DeliveryKindName: d.DeliveryKindName ?? null,
    DocumentDate: d.DocumentDate ?? null,
    EditorDocumentId: d.EditorDocumentId ?? null,
  } as IDispatchListItem);

  /** Zmiana rodzaju przesyłki: tylko nienadane i poza kartą nadawczą (jak w Magistracie). */
  const openChangeKind = (docs: IRadixListDocument[]) => {
    const eligible = docs.filter((d) => d.ParcelId && !d.DispatchChartId && !d.SendDate);
    if (eligible.length === 0) {
      notification({
        type: ENotificationType.INFO,
        title: "Nie można zmienić sposobu nadania",
        text: "Przesyłki są już nadane albo dodane do karty nadawczej.",
      });
      return;
    }
    setChangeKind(eligible.map(toDispatchItem));
  };

  const rowOptions = (doc: IRadixListDocument): Option[] => {
    const canChangeKind = !!doc.ParcelId && !doc.DispatchChartId && !doc.SendDate;
    return [
      { name: "Podgląd", callback: () => setPreview({ parcelId: doc.ParcelId!, title: doc.Title }), additional: false, isEnabled: !!doc.ParcelId, canMultiple: false },
      { name: "Edytuj w edytorze", callback: () => openInEditor(doc), additional: false, isEnabled: !!doc.EditorDocumentId && !isSent, canMultiple: false },
      { name: "Drukuj", callback: () => setPreview({ parcelId: doc.ParcelId!, title: doc.Title }), additional: false, isEnabled: !!doc.ParcelId, canMultiple: false },
      {
        name: "Utwórz kopie decyzji dla wszystkich odbiorców",
        callback: () => doGenerateForAll(doc),
        additional: false,
        isEnabled: doc.Program === ERadixProgram.PoGrun && doc.HasOtherReceivers && isProgramView,
        canMultiple: false,
        splitterBefore: true,
      },
      { name: "Zmień rodzaj przesyłki", callback: () => openChangeKind([doc]), additional: false, isEnabled: canChangeKind && !isSent, canMultiple: false },
      {
        name: "Przekaż do wysyłki",
        callback: () => run(setReady, { DocumentIds: [doc.Id] }, "Przekazano do wysyłki"),
        additional: false,
        isEnabled: isProgramView,
        canMultiple: false,
      },
      {
        name: "Oznacz jako wysłane",
        callback: () => run(markAsSent, { DocumentIds: [doc.Id] }, "Oznaczono jako wysłane", "Czy chcesz oznaczyć wybrany dokument jako wysłany?"),
        additional: false,
        isEnabled: isToSend,
        canMultiple: false,
      },
      {
        name: "Karta nadawcza (biuro podawcze)",
        callback: () => navigate("/biuro-podawcze/outgoing"),
        additional: false,
        isEnabled: isToSend && !doc.DeliveryKindIsElectronic,
        canMultiple: false,
      },
      {
        name: "Zwróć do decyzji / upomnień / zawiadomień",
        callback: () => run(returnToProject, { DocumentIds: [doc.Id] }, "Cofnięto", "Czy chcesz cofnąć wybrany dokument na listę zaimportowanych?"),
        additional: false,
        isEnabled: isToSend,
        canMultiple: false,
      },
      {
        name: "Zwróć do wysłania",
        callback: () => run(returnToSend, { DocumentIds: [doc.Id] }, "Cofnięto do wysłania", "Czy chcesz cofnąć wybrany dokument do wysyłki?"),
        additional: false,
        isEnabled: isSent,
        canMultiple: false,
      },
      {
        name: "Usuń",
        callback: () => run(remove, { DocumentIds: [doc.Id] }, "Usunięto", "Czy chcesz usunąć wybrany dokument?"),
        additional: false,
        isEnabled: !isSent,
        canMultiple: false,
        splitterBefore: true,
      },
    ];
  };

  const renderRow = (doc: IRadixListDocument) => (
    <label
      key={doc.Id}
      className={styles.row}
      title="Dwuklik — podgląd pisma"
      onDoubleClick={() => doc.ParcelId && setPreview({ parcelId: doc.ParcelId, title: doc.Title })}
    >
      <input type="checkbox" checked={selected.has(doc.Id)} onChange={() => toggle(doc.Id)} />
      {doc.HasOtherReceivers && <span className={styles.dot} title="Decyzja ma innych współwłaścicieli bez własnej kopii" />}
      <div className={styles.rowMain}>
        <div className={styles.rowTitle}>
          {doc.ReceiverName ?? "—"}
          {doc.ParcelNumber && <span className={styles.sig}>{doc.ParcelNumber}</span>}
          {doc.InternalStatus === ERadixInternalStatus.ReadyToDigitalSign && <span className={styles.badgeWarn}>{doc.InternalStatusName}</span>}
          {doc.DispatchChartSignature && <span className={styles.badge}>karta {doc.DispatchChartSignature}</span>}
        </div>
        <div className={styles.rowSub}>
          {doc.Title ?? ""}
          {doc.ReceiverAddress ? ` · ${doc.ReceiverAddress}` : ""}
          {doc.CaseSignature ? ` · teczka ${doc.CaseSignature}` : ""}
          {doc.InternalNumber ? ` · ${doc.InternalNumber}` : ""}
        </div>
      </div>
      <div className={styles.rowMeta}>
        <div>{fmtDate(isSent ? doc.SendDate : doc.DocumentDate)}</div>
        <div>{doc.DeliveryKindName ?? "brak rodzaju przesyłki"}</div>
      </div>
      <RadixRowMenu options={rowOptions(doc)} />
    </label>
  );

  const renderDocuments = (batch: IRadixListBatch) => {
    if (!isToSend && !isSent) return <div className={styles.rows}>{batch.Documents.map(renderRow)}</div>;

    // Do wysłania / Wysłane: grupowanie po rodzaju przesyłki (jak ParcelInfo w Magistracie).
    const groups = new Map<string, IRadixListDocument[]>();
    for (const d of batch.Documents) {
      const k = d.DeliveryKindName ?? "Brak rodzaju przesyłki";
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(d);
    }
    return (
      <>
        {[...groups.entries()].map(([kind, docs]) => (
          <div key={kind} className={styles.kindGroup}>
            <div className={styles.kindHead}>
              {kind} ({docs.length})
              {docs.some((d) => d.DispatchChartId) && <span className={styles.badge}>na karcie nadawczej</span>}
            </div>
            <div className={styles.rows}>{docs.map(renderRow)}</div>
          </div>
        ))}
      </>
    );
  };

  const batchActions = (batch: IRadixListBatch) => (
    <div className={styles.batchActions}>
      {batch.HasPrintFile && (
        <HollowButton
          text="Drukuj paczkę"
          onClick={() => setPrintImport({ id: batch.Id, name: batch.GroupName })}
        />
      )}
      {isProgramView && (
        <HollowButton
          text="Przekaż do wysyłki"
          onClick={() => run(setReady, { ImportId: batch.Id }, "Przekazano do wysyłki", "Czy chcesz przekazać wybraną paczkę dokumentów do wysyłki?")}
        />
      )}
      {isToSend && (
        <>
          <HollowButton
            text="Oznacz jako wysłane"
            onClick={() => run(markAsSent, { ImportId: batch.Id }, "Oznaczono jako wysłane", "Czy chcesz oznaczyć wybraną paczkę jako wysłaną?")}
          />
          <HollowButton
            text="Zwróć"
            onClick={() => run(returnToProject, { ImportId: batch.Id }, "Cofnięto", "Czy chcesz cofnąć wybraną paczkę na listę zaimportowanych dokumentów?")}
          />
        </>
      )}
      {isSent && (
        <HollowButton
          text="Zwróć do wysłania"
          onClick={() => run(returnToSend, { ImportId: batch.Id }, "Cofnięto do wysłania", "Czy chcesz cofnąć wybraną paczkę do wysyłki?")}
        />
      )}
      {isProgramView && (
        <HollowButton
          text="Usuń paczkę"
          onClick={() => run(remove, { ImportId: batch.Id }, "Usunięto", "Czy chcesz usunąć całą paczkę dokumentów?")}
        />
      )}
    </div>
  );

  return (
    <>
      <div className={styles.topBar}>
        <div className={styles.topInfo}>
          {selected.size > 0 ? `Zaznaczono: ${selected.size}` : `Paczek: ${batches.length} · pism: ${allDocs.length}`}
          {isProgramView && settings && !(settings.HasWip || settings.HasPogrun || settings.HasGok) && (
            <span className={styles.warn}>Brak połączenia z bazą RADIX — skonfiguruj w ustawieniach.</span>
          )}
        </div>
        <div className={styles.topActions}>
          {selectedDocs.length > 0 && isProgramView && (
            <HollowButton text="Przekaż do wysyłki" onClick={() => run(setReady, { DocumentIds: selectedDocs.map((d) => d.Id) }, "Przekazano do wysyłki")} />
          )}
          {selectedDocs.length > 0 && (isProgramView || isToSend) && (
            <HollowButton
              text="Zmień rodzaj przesyłki"
              onClick={() => openChangeKind(selectedDocs)}
            />
          )}
          {selectedDocs.length > 0 && isToSend && (
            <HollowButton
              text="Oznacz jako wysłane"
              onClick={() => run(markAsSent, { DocumentIds: selectedDocs.map((d) => d.Id) }, "Oznaczono jako wysłane", "Czy chcesz oznaczyć wybrane dokumenty jako wysłane?")}
            />
          )}
          {selectedDocs.length > 0 && isSent && (
            <HollowButton
              text="Zwróć do wysłania"
              onClick={() => run(returnToSend, { DocumentIds: selectedDocs.map((d) => d.Id) }, "Cofnięto do wysłania", "Czy chcesz cofnąć wybrane dokumenty do wysyłki?")}
            />
          )}
          {selectedDocs.length > 0 && !isSent && (
            <HollowButton
              text="Usuń"
              onClick={() => run(remove, { DocumentIds: selectedDocs.map((d) => d.Id) }, "Usunięto", `Czy chcesz usunąć wybrane dokumenty (${selectedDocs.length})?`)}
            />
          )}
          {isToSend && (
            <HollowButton text="Karty nadawcze" onClick={() => navigate("/biuro-podawcze/outgoing")} />
          )}
          {isProgramView && program !== ERadixProgram.GOK && (
            <HollowButton
              text="Lista w RADIX"
              onClick={() => setShowSource(true)}
              disabled={!settings || (program === ERadixProgram.PoGrun ? !settings.HasPogrun : !settings.HasWip)}
            />
          )}
          {settings?.CanManage && (
            <button type="button" className={styles.expandBtn} title="Ustawienia RADIX" onClick={() => setShowSettings(true)}>
              <FontAwesomeIcon icon={faGear} />
            </button>
          )}
          {isProgramView && (
            <Button text={IMPORT_LABEL[program!]} onClick={() => setShowImport(true)} />
          )}
        </div>
      </div>

      <div className={styles.content}>
        {isLoading ? (
          <div className={styles.empty}>Ładowanie…</div>
        ) : batches.length === 0 ? (
          <div className={styles.empty}>
            {isProgramView ? (
              <>
                Brak zaimportowanych dokumentów.
                <br />
                <FontAwesomeIcon icon={faFileImport} /> Użyj przycisku „{IMPORT_LABEL[program!]}", wskazując wydruk RTF z programu RADIX.
              </>
            ) : isToSend ? (
              "Brak dokumentów do wysłania — przekaż paczkę do wysyłki z listy decyzji, upomnień albo zawiadomień."
            ) : (
              "Brak wysłanych dokumentów w tym roku."
            )}
          </div>
        ) : (
          <div className={styles.batches}>
            {batches.map((batch) => {
              const open = !collapsed.has(batch.Id);
              return (
                <div key={batch.Id} className={styles.batchCard}>
                  <div className={styles.batchHead}>
                    <button type="button" className={styles.expandBtn} onClick={() => toggleCollapsed(batch.Id)} title={open ? "Zwiń" : "Rozwiń"}>
                      <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} />
                    </button>
                    <div className={styles.batchMain}>
                      <div className={styles.batchTitle}>
                        {batch.GroupName || "Paczka dokumentów"}
                        <span className={`${styles.badge} ${styles.badgeGray}`}>{PROGRAM_LABEL[batch.Program]}</span>
                        {batch.MajorSignature && <span className={styles.sig}>{batch.MajorSignature}</span>}
                        <span className={styles.badge}>{batch.Documents.length}</span>
                        {batch.HasPrintFile && <FontAwesomeIcon icon={faPrint} title="Paczka ma zbiorczy PDF do druku" style={{ color: "#5d49c7" }} />}
                      </div>
                      <div className={styles.batchMeta}>
                        zaimportowano {fmtDate(batch.CreationDate)} · dokumentów: {batch.Imported}
                        {batch.NotFound > 0 ? ` · nie znaleziono w RADIX: ${batch.NotFound}` : ""}
                        {batch.Skipped > 0 ? ` · pominięto: ${batch.Skipped}` : ""}
                        {isTeamView && batch.AuthorName ? ` · autor: ${batch.AuthorName}` : ""}
                      </div>
                    </div>
                    {batchActions(batch)}
                  </div>
                  {open && renderDocuments(batch)}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showImport && program !== null && (
        <ImportRadixModal
          program={program}
          places={settings?.Places ?? []}
          onClose={() => setShowImport(false)}
          onImported={() => {
            setShowImport(false);
            refetch();
            invalidate();
          }}
        />
      )}
      {showSettings && <RadixSettingsModal onClose={() => setShowSettings(false)} />}
      {showSource && program !== null && (
        <RadixSourceListModal program={program} onClose={() => setShowSource(false)} />
      )}
      {preview && <ParcelPreviewModal parcelId={preview.parcelId} title={preview.title} onClose={() => setPreview(null)} />}
      {printImport && <RadixPrintModal importId={printImport.id} title={printImport.name} onClose={() => setPrintImport(null)} />}
      {changeKind && changeKind.length > 0 && (
        <ChangeDeliveryKindModal
          items={changeKind}
          onClose={() => setChangeKind(null)}
          onDone={() => {
            setChangeKind(null);
            invalidate();
          }}
        />
      )}
    </>
  );
};

export default RadixList;
